/**
 * BFF Fetch - Minimal HTTP Client for Backend-For-Frontend Routes
 *
 * A lightweight fetch wrapper for client components to call BFF routes.
 * The BFF routes handle all backend complexity (auth, refresh, errors).
 * This only handles client-side concerns.
 *
 * Philosophy:
 * - Keep it minimal - BFF does the heavy lifting
 * - Only handle client-side necessities (credentials, body formatting)
 * - Type safety for better DX
 * - Let BFF handle all backend concerns (auth, refresh, timeouts)
 *
 * Usage:
 * ```typescript
 * import { bffFetch } from '@/lib/utils/bff-fetch';
 *
 * // In client components
 * const users = await bffFetch<User[]>('/api/users');
 *
 * // With body
 * const newUser = await bffFetch<User>('/api/users', {
 *   method: 'POST',
 *   body: { name: 'John', email: 'john@example.com' }
 * });
 *
 * // With TanStack Query
 * useQuery({
 *   queryKey: ['users'],
 *   queryFn: () => bffFetch<User[]>('/api/users')
 * });
 * ```
 */

import { BFF_LATENCY } from "@/lib/analytics/events";
import type { BffErrorData, BffFetchOptions } from "@/types/central";
import { SERVER_ERROR_SUPPORT_MESSAGE } from "@/lib/utils/error-display";
import { logger } from "@/lib/utils/logger";

// ============================================================================
// PostHog BFF Latency Tracking
// ============================================================================

/**
 * Fire-and-forget latency capture to PostHog (client-side only).
 * Uses a dynamic import so the server bundle never pulls in posthog-js.
 */
function captureBffLatency(
  endpoint: string,
  method: string,
  startTime: number,
  status: number,
): void {
  if (typeof window === "undefined") return;
  const duration_ms = Math.round(performance.now() - startTime);

  import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.capture(BFF_LATENCY, {
        endpoint,
        method,
        duration_ms,
        status,
      });
    })
    .catch(() => {
      // Silently fail — never break the app for telemetry
    });
}

// ============================================================================
// Types
// ============================================================================

/**
 * Typed error for BFF requests
 * Preserves the full error structure from backend via BFF
 */
export class BffError extends Error implements BffErrorData {
  constructor(
    message: string,
    public readonly status: number,
    public readonly endpoint: string,
    public readonly body: unknown, // Full error body from backend (preserves structure)
  ) {
    super(message);
    this.name = "BffError";
  }
}

// ============================================================================
// Server-Side URL Resolution
// ============================================================================

/**
 * Build an absolute origin URL for server-side fetches.
 * Node.js fetch rejects relative paths; browsers accept them.
 */
function buildServerOrigin(
  host: string | null,
  protocol: string | null,
): string {
  if (host) {
    const resolvedProtocol =
      protocol ?? (process.env.NODE_ENV === "production" ? "https" : "http");
    return `${resolvedProtocol}://${host}`;
  }

  const port = process.env.PORT ?? "3000";
  return `http://localhost:${port}`;
}

/**
 * Resolve a relative BFF path to an absolute URL during SSR/RSC.
 * On the client, relative paths are returned unchanged.
 */
async function resolveBffEndpoint(endpoint: string): Promise<string> {
  if (
    endpoint.startsWith("http://") ||
    endpoint.startsWith("https://") ||
    typeof window !== "undefined"
  ) {
    return endpoint;
  }

  try {
    const { headers } = await import("next/headers");
    const headerStore = await headers();
    const origin = buildServerOrigin(
      headerStore.get("x-forwarded-host") ?? headerStore.get("host"),
      headerStore.get("x-forwarded-proto"),
    );
    return `${origin}${endpoint}`;
  } catch {
    // headers() is unavailable outside a request context (e.g. tests).
    return `${buildServerOrigin(null, null)}${endpoint}`;
  }
}

/**
 * Forward incoming request cookies when bffFetch runs during SSR.
 * credentials: "include" only applies in the browser.
 */
async function getServerCookieHeader(): Promise<string | undefined> {
  try {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    const cookieHeader = cookieStore
      .getAll()
      .map(({ name, value }) => `${name}=${value}`)
      .join("; ");
    return cookieHeader || undefined;
  } catch {
    return undefined;
  }
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Fetch data from BFF routes with proper credentials and error handling
 *
 * This is a minimal wrapper that only handles client-side concerns:
 * - Sends httpOnly cookies to BFF (credentials: 'include')
 * - Formats request body (JSON or FormData)
 * - Parses JSON responses
 * - Throws typed errors
 * - Optional client-side timeout for better UX
 *
 * The BFF route handles all backend concerns:
 * - Authentication (reading tokens, adding headers)
 * - Token refresh (on 401)
 * - Backend communication
 * - Error formatting
 *
 * @param endpoint - BFF route path (e.g., '/api/users')
 * @param options - Request options (method, body, headers, etc.)
 * @returns Promise resolving to parsed JSON response
 * @throws {BffError} When BFF returns error response
 */
export async function bffFetch<T = unknown>(
  endpoint: string,
  options: BffFetchOptions = {},
): Promise<T> {
  const isDevelopment = process.env.NODE_ENV === "development";
  const requestedTimeout = options.timeout ?? 15000;
  // In development, first-request Turbopack compiles can be slow and trigger false timeouts.
  // Keep production behavior unchanged while applying a much safer dev minimum.
  const timeout = isDevelopment
    ? Math.max(requestedTimeout, 300000)
    : requestedTimeout;
  const { body, signal: externalSignal, ...fetchOptions } = options;

  // Prepare request body and headers
  let requestBody: string | FormData | undefined;
  const headers: Record<string, string> = {};

  // Copy existing headers
  if (fetchOptions.headers) {
    if (fetchOptions.headers instanceof Headers) {
      fetchOptions.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (typeof fetchOptions.headers === "object") {
      Object.assign(headers, fetchOptions.headers);
    }
  }

  // Format body: FormData passes through, strings pass through, objects become JSON
  if (body instanceof FormData) {
    requestBody = body;
    // Browser sets Content-Type with boundary automatically
  } else if (typeof body === "string") {
    requestBody = body;
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  } else if (body) {
    requestBody = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }

  // Client-side timeout (optional - for better UX than waiting for BFF timeout)
  const controller = new AbortController();
  let didTimeout = false;
  const hasClientTimeout = Number.isFinite(timeout) && timeout > 0;
  const timeoutId = hasClientTimeout
    ? setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, timeout)
    : null;

  // Combine external signal (e.g. from TanStack Query) with timeout signal
  const signals: AbortSignal[] = [controller.signal];
  if (externalSignal) signals.push(externalSignal);
  const combinedSignal =
    signals.length > 1 ? AbortSignal.any(signals) : controller.signal;

  const startTime = performance.now();

  try {
    const resolvedEndpoint = await resolveBffEndpoint(endpoint);

    if (typeof window === "undefined") {
      const cookieHeader = await getServerCookieHeader();
      if (cookieHeader) {
        headers.Cookie = cookieHeader;
      }
    }

    // Make request to BFF route
    const response = await fetch(resolvedEndpoint, {
      ...fetchOptions,
      body: requestBody,
      headers,
      credentials: "include", // CRITICAL: Send httpOnly cookies to BFF
      cache: "no-store", // Always fetch fresh data
      signal: combinedSignal,
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    captureBffLatency(
      endpoint,
      fetchOptions.method || "GET",
      startTime,
      response.status,
    );

    // BFF already formatted errors - preserve full structure
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));

      // Extract message for Error.message (for display).
      // 5xx bodies stay in logs; the thrown message is customer-facing.
      let message = "Request failed";
      if (response.status >= 500) {
        message = SERVER_ERROR_SUPPORT_MESSAGE;
      } else if (typeof errorBody === "object" && errorBody !== null) {
        if ("message" in errorBody && typeof errorBody.message === "string") {
          message = errorBody.message;
        } else if (
          "error" in errorBody &&
          typeof errorBody.error === "string"
        ) {
          message = errorBody.error;
        } else if ("detail" in errorBody) {
          // Handle FastAPI validation errors: detail can be string or array
          if (typeof errorBody.detail === "string") {
            message = errorBody.detail;
          } else if (
            Array.isArray(errorBody.detail) &&
            errorBody.detail.length > 0
          ) {
            // Extract first validation error message
            const firstError = errorBody.detail[0];
            if (typeof firstError === "object" && firstError !== null) {
              const field = Array.isArray(firstError.loc)
                ? firstError.loc[firstError.loc.length - 1]
                : "field";
              const msg =
                firstError.msg || firstError.message || "Invalid value";
              message = `${field}: ${msg}`;
            } else if (typeof firstError === "string") {
              message = firstError;
            }
          }
        }
      }

      // Create typed error for programmatic handling
      const bffError = new BffError(
        message,
        response.status,
        endpoint,
        errorBody,
      );

      // Log based on status code severity:
      // - 5xx: Server errors - always log as errors
      // - 4xx: Client errors - log as warnings (often expected, e.g., 404 not found)
      const logContext = {
        endpoint,
        status: response.status,
        method: fetchOptions.method || "GET",
      };

      if (response.status >= 500) {
        logger.error("BFF server error", bffError, logContext);
      } else if (response.status !== 404) {
        // Log 4xx errors as warnings, but skip 404s (often expected)
        logger.warn(`BFF request failed (${response.status})`, logContext);
      }

      // Preserve full error structure for programmatic handling
      throw bffError;
    }

    // 204 No Content — nothing to parse (e.g. DELETE responses)
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse and return successful response
    return (await response.json()) as T;
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Handle abort — distinguish timeout from external cancellation
    if (error instanceof Error && error.name === "AbortError") {
      if (didTimeout) {
        const timeoutError = new BffError("Request timeout", 408, endpoint, {
          error: "Request timeout",
          message: "Request timeout",
        });
        logger.error("BFF request timeout", timeoutError, {
          endpoint,
          timeout,
          method: fetchOptions.method || "GET",
        });
        throw timeoutError;
      }

      // External signal aborted (e.g. TanStack Query unmount) — re-throw
      // silently so the query library can handle cancellation without noise.
      throw error;
    }

    // Re-throw BffError as-is
    if (error instanceof BffError) {
      throw error;
    }

    // Handle network errors with better error messages
    if (error instanceof Error) {
      const errorMessage = error.message;

      // Detect common network error patterns and provide helpful messages
      if (
        errorMessage === "Failed to fetch" ||
        errorMessage.includes("fetch failed") ||
        errorMessage.includes("NetworkError") ||
        errorMessage.includes("Network request failed")
      ) {
        // Check if endpoint is relative (should work) or absolute (might have CORS issues)
        const isRelativeUrl = endpoint.startsWith("/");
        const isAbsoluteUrl =
          endpoint.startsWith("http://") || endpoint.startsWith("https://");

        let helpfulMessage = "Network request failed";

        if (isAbsoluteUrl) {
          helpfulMessage = `Failed to connect to ${endpoint}. This may be due to:\n- The server is not running\n- CORS configuration issues\n- Network connectivity problems`;
        } else if (isRelativeUrl) {
          helpfulMessage = `Failed to connect to ${endpoint}. This may be due to:\n- The Next.js server is not running\n- The API route does not exist\n- Network connectivity problems`;
        }

        const networkError = new BffError(helpfulMessage, 0, endpoint, {
          error: errorMessage,
          message: helpfulMessage,
          originalError: errorMessage,
        });
        logger.error("BFF network error", networkError, {
          endpoint,
          method: fetchOptions.method || "GET",
          originalError: errorMessage,
        });
        throw networkError;
      }

      // Response may already have been received (JSON parse, unexpected
      // throw). Status 0 is reserved for connection failures so mutations
      // do not retry a write that already landed.
      const otherError = new BffError(errorMessage, 500, endpoint, {
        error: errorMessage,
        message: errorMessage,
      });
      logger.error("BFF request error", otherError, {
        endpoint,
        method: fetchOptions.method || "GET",
      });
      throw otherError;
    }

    // Unknown error type — not a transport failure.
    const unknownError = new BffError("Unknown error occurred", 500, endpoint, {
      error: "Unknown error",
      message: "Unknown error occurred",
    });
    logger.error("BFF unknown error", unknownError, {
      endpoint,
      method: fetchOptions.method || "GET",
    });
    throw unknownError;
  }
}

// ============================================================================
// Convenience Wrappers
// ============================================================================

/**
 * GET request to BFF route
 */
export async function bffGet<T = unknown>(
  endpoint: string,
  params?: Record<string, string | number | boolean>,
  options?: Omit<BffFetchOptions, "method" | "body">,
): Promise<T> {
  const url = params
    ? `${endpoint}?${new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)]),
      )}`
    : endpoint;

  return bffFetch<T>(url, { ...options, method: "GET" });
}

/**
 * POST request to BFF route
 */
export async function bffPost<T = unknown>(
  endpoint: string,
  body?: Record<string, unknown> | FormData,
  options?: Omit<BffFetchOptions, "method" | "body">,
): Promise<T> {
  return bffFetch<T>(endpoint, { ...options, method: "POST", body });
}

/**
 * PUT request to BFF route
 */
export async function bffPut<T = unknown>(
  endpoint: string,
  body?: Record<string, unknown> | FormData,
  options?: Omit<BffFetchOptions, "method" | "body">,
): Promise<T> {
  return bffFetch<T>(endpoint, { ...options, method: "PUT", body });
}

/**
 * PATCH request to BFF route
 */
export async function bffPatch<T = unknown>(
  endpoint: string,
  body?: Record<string, unknown> | FormData,
  options?: Omit<BffFetchOptions, "method" | "body">,
): Promise<T> {
  return bffFetch<T>(endpoint, { ...options, method: "PATCH", body });
}

/**
 * DELETE request to BFF route
 */
export async function bffDelete<T = unknown>(
  endpoint: string,
  options?: Omit<BffFetchOptions, "method" | "body">,
): Promise<T> {
  return bffFetch<T>(endpoint, { ...options, method: "DELETE" });
}

// ============================================================================
// Type Guards & Helpers
// ============================================================================

/**
 * Type guard to check if error is a BffError
 */
export function isBffError(error: unknown): error is BffError {
  return error instanceof BffError;
}

/**
 * Get error message from BffError
 *
 * The BFF route already formatted the error from the backend.
 * We trust the backend's error message - it knows the context best.
 * Only add optional context prefix for better UX.
 *
 * @param error - The error to get message from
 * @param context - Optional action context (e.g., "saving user")
 * @returns User-friendly error message
 */
export function getBffErrorMessage(error: unknown, context?: string): string {
  if (!isBffError(error)) {
    return "An unexpected error occurred. Please try again.";
  }

  // Backend already provided a good error message
  // Just add context if provided
  const prefix = context ? `Error ${context}: ` : "";
  return prefix + error.message;
}

/**
 * Get error code from BffError body
 * Useful for programmatic error handling
 *
 * @param error - The BffError to extract code from
 * @returns Error code string or undefined
 */
export function getBffErrorCode(error: BffError): string | undefined {
  if (typeof error.body === "object" && error.body !== null) {
    if (
      "error_code" in error.body &&
      typeof error.body.error_code === "string"
    ) {
      return error.body.error_code;
    }
    if ("code" in error.body && typeof error.body.code === "string") {
      return error.body.code;
    }
  }
  return undefined;
}

/**
 * Get field validation errors from BffError body
 * For 422 validation errors with field-specific details
 *
 * @param error - The BffError to extract field errors from
 * @returns Array of field errors or empty array
 */
export function getBffFieldErrors(error: BffError): Array<{
  field: string;
  message: string;
}> {
  if (typeof error.body === "object" && error.body !== null) {
    // FastAPI format: { detail: [{ loc: ["body", "field"], msg: "..." }] }
    if ("detail" in error.body && Array.isArray(error.body.detail)) {
      return error.body.detail
        .map((item: unknown): { field: string; message: string } | null => {
          if (typeof item === "object" && item !== null) {
            const errorItem = item as {
              loc?: unknown[];
              msg?: string;
              message?: string;
            };
            const field = Array.isArray(errorItem.loc)
              ? String(errorItem.loc[errorItem.loc.length - 1])
              : "unknown";
            const message =
              errorItem.msg || errorItem.message || "Invalid value";
            return { field, message: String(message) };
          }
          return null;
        })
        .filter(
          (item): item is { field: string; message: string } => item !== null,
        );
    }

    // Generic format: { errors: { field: "message" } }
    if (
      "errors" in error.body &&
      typeof error.body.errors === "object" &&
      error.body.errors !== null
    ) {
      const errors = error.body.errors as Record<string, unknown>;
      return Object.entries(errors).map(([field, message]) => ({
        field,
        message: String(message),
      }));
    }
  }
  return [];
}
